import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip, IconButton, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import DateRangeFilter from "../components/common/DateRangeFilter";
import StandardTable from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";

import useDebounce from "../hooks/useDebounce";
import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import { usePermissions } from "../hooks/usePermissions";
import { getReceipts } from "../redux/actions/receiptActions";

const ReceiptsPage = () => {
  const dispatch = useDispatch();
  const { navigateToTab } = useWorkspaceNavigation();
  const { hasPermission } = usePermissions();

  const today = new Date().toISOString().split("T")[0];

  // Redux State
  const { receipts, pagination, loadingReceipts, errorLoadingReceipts } =
    useSelector((state) => state.receipt);

  // Local State
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  const canRead = hasPermission("receipt:read_all");

  const fetchReceiptsList = useCallback(() => {
    if (!canRead) return;
    dispatch(
      getReceipts({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    startDate,
    endDate,
    canRead,
  ]);

  useEffect(() => {
    fetchReceiptsList();
  }, [fetchReceiptsList]);

  // Reset to page 1 when search or dates change
  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch, startDate, endDate]);

  const handleViewDetails = (receipt) => {
    navigateToTab(`/receipts/${receipt.id}`);
  };

  const formatPlainNumber = (val) =>
    Number(val || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // --- Table Configuration aligned with ReceiptSummaryDto ---
  const columns = useMemo(
    () => [
      {
        id: "transactionNumber",
        label: "Receipt #",
        minWidth: 130,
        format: (val) => <b style={{ color: "#4cc9f0" }}>{val}</b>,
      },
      {
        id: "invoiceNumber",
        label: "Invoice #",
        minWidth: 130,
        format: (val) =>
          val ? (
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {val}
            </Typography>
          ) : (
            "N/A"
          ),
      },
      {
        id: "customerBusinessName",
        label: "Customer",
        minWidth: 180,
      },
      {
        id: "receiptDateTime",
        label: "Date & Time",
        minWidth: 160,
        format: (val) => new Date(val).toLocaleString(),
      },
      {
        id: "transactionStatus",
        label: "Status",
        minWidth: 120,
        format: (val) => <StatusChip status={val} />,
      },
      {
        id: "amountPaid",
        label: "Amount Paid",
        minWidth: 120,
        align: "right",
        format: (val) => (
          <Typography variant="body2" fontWeight="bold" color="success.main">
            {formatPlainNumber(val)}
          </Typography>
        ),
      },
    ],
    [],
  );

  return (
    <PageContainer
      title="Transaction Receipts"
      subtitle="View and manage payment receipts allocated to license invoices."
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by receipt or invoice number..."
      >
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>

      <StandardTable
        columns={columns}
        data={receipts}
        loading={loadingReceipts}
        error={errorLoadingReceipts}
        onRetry={fetchReceiptsList}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig({ ...pageConfig, page: p })}
        onRowsPerPageChange={(l) =>
          setPageConfig({ ...pageConfig, limit: l, page: 1 })
        }
        rowAction={(row) => (
          <Tooltip title="View Receipt / Print">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleViewDetails(row)}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      />
    </PageContainer>
  );
};

export default ReceiptsPage;
